// src/pages/LandingAndDebtStep.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

/* -----------------------------
   Meta Pixel (inline helper)
----------------------------- */
const PIXEL_ID = "24921128320812912";
let _pixelLoaded = false;

function loadMetaPixel() {
  if (typeof window === "undefined") return;

  if (!_pixelLoaded) {
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = "https://connect.facebook.net/en_US/fbevents.js";
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, "script");

    _pixelLoaded = true;
    window.fbq("init", PIXEL_ID);
  }

  // Fire PageView every time this page mounts
  window.fbq("track", "PageView");
}

// Custom events helper
function fbqTrackCustom(event, params = {}) {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("trackCustom", event, params);
  }
}

/* -----------------------------
   Brand & Data
----------------------------- */
const BRAND_BLUE = "#0057B8";

const DEBT_OPTIONS = [
  "$1-$9,999",
  "$10,000-$19,999",
  "$20,000-$29,999",
  "$30,000+",
];

const VERBIAGE = (
  <span className="font-medium text-xs sm:text-sm text-[#0057B8] mb-2 text-center block">
    <span className="uppercase"></span>
  </span>
);

const EXAMPLES = [
  { debt: "$35k", current: 925, through: 500 },
  { debt: "$35k", current: 1254, through: 583 },
  { debt: "$47k", current: 1770, through: 690 },
];

const TESTIMONIALS = [
  {
    quote:
      "I decided to follow Amend Finance's directions and I can honestly say, I have energy and drive I never thought I'd regain. I'm currently enrolled in a financial program and I'm pumped.",
    name: "Angela M.",
    location: "Phoenix, AZ",
  },
  {
    quote:
      "Amend Finance, thank you from the bottom of my heart. You are heaven sent. Any time I can be of assistance to you and your company, just say the word.",
    name: "Robert T.",
    location: "Austin, TX",
  },
  {
    quote:
      "Amend Finance made me feel much better about my outlook and I felt I was not alone.",
    name: "Sharon W.",
    location: "Seattle, WA",
  },
  {
    quote:
      "I look forward now to fixing this financial mess I'm in and I believe that has much to do with Amend Finance.",
    name: "Carlos R.",
    location: "Miami, FL",
  },
  {
    quote:
      "I wanted to express my sincere thanks for the help and guidance of Amend Finance in setting up my account. Their clear and consistent explanations have gone a great distance in allaying my concerns.",
    name: "Diana P.",
    location: "Chicago, IL",
  },
  {
    quote:
      "I'm pleased to be working with Amend Finance and I look forward to having the program play out over the coming months.",
    name: "James K.",
    location: "Denver, CO",
  },
];

/* -----------------------------
   Icons
----------------------------- */
function Tick() {
  return (
    <svg
      className="w-6 h-6 text-[#0057B8] flex-shrink-0 mr-2"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

const Icon = {
  Calculator: () => (
    <svg
      viewBox="0 0 24 24"
      className="w-7 h-7"
      fill="none"
      stroke={BRAND_BLUE}
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <rect x="7" y="6" width="10" height="3" />
      <path d="M8 12h2M8 15h2M8 18h2M12 12h2M12 15h2M12 18h2M16 12h1" />
    </svg>
  ),
  Shield: () => (
    <svg
      viewBox="0 0 24 24"
      className="w-7 h-7"
      fill="none"
      stroke={BRAND_BLUE}
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path d="M12 3l7 3v6c0 4.5-3.5 7.6-7 9-3.5-1.4-7-4.5-7-9V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  Speed: () => (
    <svg
      viewBox="0 0 24 24"
      className="w-7 h-7"
      fill="none"
      stroke={BRAND_BLUE}
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path d="M21 13a9 9 0 10-18 0" />
      <path d="M12 6v5l3 3" />
      <path d="M3.5 13h17" />
    </svg>
  ),
  Handshake: () => (
    <svg
      viewBox="0 0 24 24"
      className="w-7 h-7"
      fill="none"
      stroke={BRAND_BLUE}
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path d="M8 12l2-2 2 2 2-2 2 2" />
      <path d="M2 12l4-4h4l2 2m10 2l-4 4h-4l-2-2" />
      <path d="M6 16l3 3m9-3l-3 3" />
    </svg>
  ),
};

/* -----------------------------
   Hooks & Helpers (Reveal)
----------------------------- */
function useInView(ref, options = { threshold: 0.15 }) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        obs.unobserve(entry.target);
      }
    }, options);
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, options]);
  return inView;
}

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out motion-reduce:transition-none motion-reduce:transform-none ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* -----------------------------
   Testimonials Carousel
----------------------------- */
function TestimonialsCarousel({ items, interval = 5000 }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const containerRef = useRef(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(
      () => setIdx((i) => (i + 1) % items.length),
      interval
    );
    return () => clearInterval(id);
  }, [paused, interval, items.length]);

  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
  const next = () => setIdx((i) => (i + 1) % items.length);

  const onTouchStart = (e) => (touchStartX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) (delta > 0 ? prev() : next());
    touchStartX.current = null;
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") prev();
    if (e.key === "ArrowRight") next();
  };

  return (
    <div
      ref={containerRef}
      className="relative max-w-3xl mx-auto"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onKeyDown={onKeyDown}
      tabIndex={0}
      aria-roledescription="carousel"
      aria-label="Client testimonials"
    >
      <div className="relative min-h-[200px] sm:min-h-[180px]">
        {items.map((t, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-500 ease-out ${
              i === idx ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            role="group"
            aria-label={`Testimonial ${i + 1} of ${items.length}`}
          >
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md text-center">
              <p className="text-gray-700 text-sm sm:text-base italic mb-4 sm:mb-6">
                “{t.quote}”
              </p>
              <h4 className="font-bold text-[#0057B8] text-sm sm:text-base">
                {t.name} – {t.location}
              </h4>
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-1">
        <button
          type="button"
          onClick={prev}
          className="bg-white/90 hover:bg-white border border-[#e9ecf3] rounded-full w-9 h-9 grid place-items-center shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD600]"
          aria-label="Previous testimonial"
        >
          ‹
        </button>
        <button
          type="button"
          onClick={next}
          className="bg-white/90 hover:bg-white border border-[#e9ecf3] rounded-full w-9 h-9 grid place-items-center shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD600]"
          aria-label="Next testimonial"
        >
          ›
        </button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-6">
        {items.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Go to testimonial ${i + 1}`}
            aria-current={idx === i ? "true" : "false"}
            className={`w-3 h-3 rounded-full transition ${
              idx === i ? "bg-[#0057B8]" : "bg-gray-300 hover:bg-gray-400"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

/* -----------------------------
   Examples Carousel (with gutter)
----------------------------- */
function ExamplesCarousel({ items, interval = 5000 }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const outerRef = useRef(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(
      () => setIdx((i) => (i + 1) % items.length),
      interval
    );
    return () => clearInterval(id);
  }, [paused, interval, items.length]);

  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
  const next = () => setIdx((i) => (i + 1) % items.length);

  const onTouchStart = (e) => (touchStartX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) (delta > 0 ? prev() : next());
    touchStartX.current = null;
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") prev();
    if (e.key === "ArrowRight") next();
  };

  return (
    <div
      ref={outerRef}
      className="relative max-w-3xl mx-auto"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onKeyDown={onKeyDown}
      tabIndex={0}
      aria-roledescription="carousel"
      aria-label="Real client examples"
    >
      {/* Gutter + extra bottom padding for dots */}
      <div className="relative px-8 sm:px-12 pb-10 sm:pb-12">
        {/* Slides */}
        <div className="relative min-h-[260px] sm:min-h-[240px]">
          {items.map((x, i) => {
            const saved = x.current - x.through;
            const savedPct = Math.round((saved / x.current) * 100);
            return (
              <div
                key={i}
                className={`absolute inset-0 transition-opacity duration-500 ease-out ${
                  i === idx ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                role="group"
                aria-label={`Example ${i + 1} of ${items.length}`}
              >
                <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md">
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] sm:text-xs uppercase tracking-wide text-[#6B7280]">
                      Balance
                    </div>
                    <span className="text-[10px] sm:text-[11px] px-2 py-0.5 rounded-full bg-[#ecfdf5] text-[#065f46] font-semibold">
                      Save {savedPct}%
                    </span>
                  </div>

                  <div className="text-2xl sm:text-3xl font-extrabold text-[#0f172a] mt-1">
                    {x.debt}
                  </div>

                  <div className="mt-3 flex flex-col space-y-2">
                    <div className="rounded-lg px-3 py-2 bg-[#FAFBFF] border border-[#e9ecf3] text-left">
                      <div className="text-[11px] text-[#6B7280]">
                        Current Monthly Payment
                      </div>
                      <div className="text-lg font-semibold text-[#1f2937]">
                        ${x.current.toLocaleString()}
                      </div>
                    </div>
                    <div className="rounded-lg px-3 py-2 bg-[#ECFDF5] border border-[#d7f3e7] text-left">
                      <div className="text-[11px] text-[#065f46] font-semibold">
                        Through Amend
                      </div>
                      <div className="text-xl font-extrabold text-[#065f46]">
                        ${x.through.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Controls anchored in the gutter */}
        <div className="pointer-events-none">
          <button
            type="button"
            onClick={prev}
            className="pointer-events-auto absolute top-1/2 -translate-y-1/2 left-0 bg-white/90 hover:bg-white border border-[#e9ecf3] rounded-full w-9 h-9 grid place-items-center shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD600]"
            aria-label="Previous example"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={next}
            className="pointer-events-auto absolute top-1/2 -translate-y-1/2 right-0 bg-white/90 hover:bg-white border border-[#e9ecf3] rounded-full w-9 h-9 grid place-items-center shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD600]"
            aria-label="Next example"
          >
            ›
          </button>
        </div>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-8 sm:mt-10">
        {items.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Go to example ${i + 1}`}
            aria-current={idx === i ? "true" : "false"}
            className={`w-3 h-3 rounded-full transition ${
              idx === i ? "bg-[#0057B8]" : "bg-gray-300 hover:bg-gray-400"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

/* =======================================================
   Page
======================================================= */
export default function LandingAndDebtStep() {
  const navigate = useNavigate();
  const [debtAmount, setDebtAmount] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    // Load Meta Pixel + PageView
    loadMetaPixel();

    // Page-level ViewContent with useful context
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "ViewContent", {
        content_name: "LandingAndDebtStep",
        page_path: window.location.pathname,
        page_title: "See Your Loan Options",
        content_category: "funnel",
      });
    }
  }, []);

  function handleSubmit(e) {
    e.preventDefault();

    if (!debtAmount) {
      setError("Please Select Loan Amount");
      return;
    }
    setError("");

    // Track clear custom event for analyst-friendly reporting
    fbqTrackCustom("DebtFormSubmitted", { debt_tier: debtAmount });

    const existing = (() => {
      try {
        return JSON.parse(localStorage.getItem("funnelData")) || {};
      } catch {
        return {};
      }
    })();

    localStorage.setItem(
      "funnelData",
      JSON.stringify({ ...existing, debtAmount })
    );

    navigate("/qualify");
  }

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      <main className="w-full max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-10">
        {/* Leading statement */}
        <Reveal className="text-center">
          <h2 className="font-extrabold text-[24px] sm:text-[32px] leading-tight text-[#0f172a]">
            See Your Loan Options
          </h2>
        </Reveal>
        <Reveal className="text-center">
          <p className="text-[#1A2040] text-[14px] sm:text-[17px] max-w-3xl mx-auto mt-2"></p>
          <p className="text-[#1A2040] text-[14px] sm:text-[17px] max-w-3xl mx-auto mt-2"></p>
        </Reveal>

        <div className="h-px bg-[#e9ecf3] my-6 sm:my-8" />

        {/* Ticks */}
        <Reveal className="max-w-3xl mx-auto">
          <ul className="text-[16px] sm:text-[20px] text-[#1A2040] space-y-3 sm:space-y-4 font-semibold text-center">
            <li className="flex items-center justify-center">
              <Tick /> Reduce Your Monthly Payments
            </li>
            <li className="flex items-center justify-center">
              <Tick /> Consolidate Your Debt
            </li>
            <li className="flex items-center justify-center">
              <Tick /> Free Consultation
            </li>
          </ul>
        </Reveal>

        <div className="h-px bg-[#e9ecf3] my-6 sm:my-8" />

        {/* Debt step — POP-OUT FORM CARD */}
        <Reveal className="max-w-3xl mx-auto">
          <form
            onSubmit={handleSubmit}
            autoComplete="off"
            className="space-y-4 bg-white shadow-2xl rounded-2xl p-6 sm:p-8 border border-[#e9ecf3]"
            aria-label="Debt Estimator"
          >
            <h3 className="text-[18px] sm:text-[20px] font-bold text-[#0b3d6e] text-center">
              Select Loan Amount
            </h3>
            {VERBIAGE}

            <fieldset role="radiogroup" aria-label="Debt Tiers">
              <legend className="sr-only">Choose your estimated debt amount</legend>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
                {DEBT_OPTIONS.map((option) => {
                  const selected = debtAmount === option;
                  return (
                    <label
                      key={option}
                      className={[
                        "relative cursor-pointer select-none",
                        "px-3.5 sm:px-4 py-3.5 sm:py-4 rounded-xl border text-center",
                        "bg-white",
                        "transition-transform duration-150",
                        selected
                          ? "border-[#0057B8] ring-2 ring-[#0057B8] shadow-md -translate-y-[1px] text-[#0b3d6e] font-semibold"
                          : "border-[#dfe4ee] hover:bg-[#F3F6FF] hover:shadow-sm",
                      ].join(" ")}
                    >
                      <input
                        type="radio"
                        name="debtAmount"
                        value={option}
                        checked={selected}
                        onChange={() => {
                          // Select tier
                          setDebtAmount(option);
                          setError("");

                          // Analyst-friendly custom event
                          fbqTrackCustom("DebtAmountSelected", {
                            debt_tier: option,
                          });
                        }}
                        className="sr-only"
                      />
                      <span className="block text-[14px] sm:text-[16px]">
                        {option}
                      </span>
                      <span
                        className="absolute inset-0 rounded-xl pointer-events-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#FFD600]"
                        aria-hidden="true"
                      />
                    </label>
                  );
                })}
              </div>
            </fieldset>

            {error && (
              <div
                className="text-red-600 text-sm mt-2 text-center font-semibold"
                role="alert"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-[#0057B8] hover:bg-[#003e7e] text-white font-bold py-3 rounded-xl mt-4 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={!debtAmount}
            >
              LET&apos;S GO
            </button>
          </form>
        </Reveal>

        <div className="h-px bg-[#e9ecf3] my-8 sm:my-10" />

        {/* Real Examples */}
        <section>
          <Reveal className="text-center">
            <h3 className="text-[18px] sm:text-[20px] font-extrabold text-[#0f172a] mb-4">
              Real Examples. Real Clients.
            </h3>
          </Reveal>
          <Reveal>
            <ExamplesCarousel items={EXAMPLES} />
          </Reveal>
        </section>

        <div className="h-px bg-[#e9ecf3] my-8 sm:my-10" />

        {/* Testimonials */}
        <section>
          <Reveal className="text-center">
            <h3 className="text-[18px] sm:text-[20px] font-extrabold text-[#0f172a] mb-4">
              What Our Clients Say
            </h3>
          </Reveal>
          <Reveal>
            <TestimonialsCarousel items={TESTIMONIALS} />
          </Reveal>
        </section>

        <div className="h-px bg-[#e9ecf3] my-8 sm:my-10" />

        {/* Why Amend Finance */}
        <section aria-label="Why Amend Finance" className="pb-2 sm:pb-4">
          <Reveal className="text-center">
            <h3 className="text-[18px] sm:text-[20px] font-extrabold text-[#0f172a] mb-6">
              Why Amend Finance?
            </h3>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {[
              {
                title: "Industry Expertise",
                body:
                  "You don’t have to be stuck with your current interest rate. Our experts may have better options.",
                IconCmp: Icon.Calculator,
              },
              {
                title: "Full Transparency",
                body:
                  "We offer clear explanations to help you make informed decisions. No hidden details in the fine print.",
                IconCmp: Icon.Shield,
              },
              {
                title: "Speed",
                body: "Our solutions can take place as quickly as 1–2 business days.",
                IconCmp: Icon.Speed,
              },
              {
                title: "Common Interest",
                body:
                  "We are paid by companies within our affiliated network only if we help you find a better option.",
                IconCmp: Icon.Handshake,
              },
            ].map(({ title, body, IconCmp }, i) => (
              <Reveal key={title} delay={i * 120}>
                <div className="flex items-start gap-4 p-4 bg-white/60 border border-[#e9ecf3] rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                  <div className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#E8F1FB]">
                    <IconCmp />
                  </div>
                  <div>
                    <h4 className="text-[16px] sm:text-[17px] font-bold text-[#0f172a]">
                      {title}
                    </h4>
                    <p className="text-[14px] sm:text-[15px] text-[#334155] mt-1">
                      {body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
